import * as React from 'react';
import ActivityIndicator from '~/UI/ActivityIndicator';
import CountryIcon from '~/UI/CountryIcon';
import ERPRevenueSummary from './ERPRevenueSummary';
import LoadableERPBarChart from './LoadableERPBarChart';
import LoadableERPChart from '~/MedicinalBrandsAnalytics/VirtualBrand/ERPTab/LoadableERPChart';

import { ERPInterventionRequest, ERPScenarioError } from './api/ERPInterventionRequest';
import { useApiResult } from '~/infrastructure/mam/ApiHooks';

import classNames from 'classnames';
import { parseERPApiResult } from '~/MedicinalBrandsAnalytics/VirtualBrand/ERPTab/utils/ERPApiParser';
import { IterventionTitles } from './api/ERPInterventionCreateRequest';

import type { ErpScenarioDetailResponse, ErpScenarioResponse } from 'com.cogvio.pricemonitor.api';

interface ERPInterventionScenarioProps {
	currency: string;
	erpPackId: string;
	erpSessionId: string;
	index: number;
	scenario: ErpScenarioResponse;
	virtualProductPackName: string;
	userId: string;
	onScenarioEdit: (name: string) => void;
}

const ERPInterventionScenario = ({
	currency,
	erpPackId,
	erpSessionId,
	index,
	scenario,
	virtualProductPackName,
	userId,
	onScenarioEdit,
}: ERPInterventionScenarioProps) => {
	const [active, setActive] = React.useState(true);
	const [erpScenarioResult] = useApiResult<ERPInterventionRequest, ErpScenarioDetailResponse, ERPScenarioError>(
		new ERPInterventionRequest(userId, erpPackId, erpSessionId, scenario.id),
		[userId, erpPackId, erpSessionId, scenario]
	);

	const parsedErpResult = parseERPApiResult(erpScenarioResult.value?.erpModelResult, erpScenarioResult);

	if (erpScenarioResult.isLoading) {
		return <ActivityIndicator />;
	}

	return (
		<div className='scenario-item text-left' key={scenario.id}>
			<div className={classNames('block border-b border-gray-300')}>
				<div className='relative pl-10 pt-4 pb-4'>
					<div className='mb-2 flex items-center justify-between'>
						<div className='flex items-center'>
							<div className='absolute left-0 top-3 flex items-center justify-center w-8 h-8 font-display font-semibold text-label text-gray-600 rounded-full bg-gray-100'>
								{index}#
							</div>
							<button
								type='button'
								onClick={(event) => {
									event.preventDefault();
									setActive((prev) => !prev);
								}}
							>
								<h3 className='font-display text-lg leading-tight font-semibold text-cPurple-800 tracking-wider tabular-nums transition transtion hover:text-cPurple-600 underline'>
									{scenario.name}
								</h3>
							</button>
							{scenario.interventions.map((scenario) => (
								<span
									className='ml-2 my-px inline-block px-1.5 py-1 rounded-sm border border-gray-200 font-display text-labelsmall font-medium leading-3 text-cBlue-800 tracking-wide bg-gray-100'
									key={`${scenario.country.alpha2}-${scenario.month}`}
								>
									<strong className='flex items-center font-semibold tabular-nums'>
										<CountryIcon code={scenario.country.alpha2} name={scenario.country.name} size={17} />
										&nbsp;
										{scenario.country.name}:&nbsp;{IterventionTitles.get(scenario.type)}
									</strong>
								</span>
							))}
						</div>

						<button
							className='ml-4 inline-block px-1.5 py-1 rounded-sm font-display text-labelsmall font-semibold text-cPurple-600 leading-3 bg-cPurple-600 bg-opacity-20 transition transition-all hover:text-white hover:bg-opacity-100 btn-scenario-edit'
							onClick={() => onScenarioEdit(scenario.id)}
						>
							<svg
								xmlns='http://www.w3.org/2000/svg'
								viewBox='0 0 20 20'
								fill='currentColor'
								className='inline-block relative -mt-px w-3 h-3'
							>
								<path
									fillRule='evenodd'
									d='M7.84 1.804A1 1 0 018.82 1h2.36a1 1 0 01.98.804l.331 1.652a6.993 6.993 0 011.929 1.115l1.598-.54a1 1 0 011.186.447l1.18 2.044a1 1 0 01-.205 1.251l-1.267 1.113a7.047 7.047 0 010 2.228l1.267 1.113a1 1 0 01.206 1.25l-1.18 2.045a1 1 0 01-1.187.447l-1.598-.54a6.993 6.993 0 01-1.929 1.115l-.33 1.652a1 1 0 01-.98.804H8.82a1 1 0 01-.98-.804l-.331-1.652a6.993 6.993 0 01-1.929-1.115l-1.598.54a1 1 0 01-1.186-.447l-1.18-2.044a1 1 0 01.205-1.251l1.267-1.114a7.05 7.05 0 010-2.227L1.821 7.773a1 1 0 01-.206-1.25l1.18-2.045a1 1 0 011.187-.447l1.598.54A6.993 6.993 0 017.51 3.456l.33-1.652zM10 13a3 3 0 100-6 3 3 0 000 6z'
									clipRule='evenodd'
								></path>
							</svg>
							&nbsp;Edit scenario
						</button>
					</div>
					<div className='relative pr-14 mb-1'>
						<ERPRevenueSummary currency={currency} erpModelResult={erpScenarioResult?.value?.erpModelResult} />
					</div>
				</div>
				{active && (
					<div className='pl-14 pb-10 flex justify-between'>
						<div className='grow w-full pl-10_'>
							<LoadableERPBarChart
								apiResult={erpScenarioResult}
								currency={currency}
								erpModelResult={erpScenarioResult.value?.erpModelResult}
							/>
							<LoadableERPChart
								hideSummaries
								result={parsedErpResult}
								virtualProductPackName={virtualProductPackName}
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default React.memo(ERPInterventionScenario);
